/**
 * Bill Detail Page
 * 
 * Display vendor bill with linked PO, payment history, and actions
 * 
 * Simple SO → Final Invoice flow:
 * - amount_cents: Original/initial amount (often from SO/PO)
 * - is_final: True when vendor's final invoice is received
 * - final_amount_cents: The actual invoice amount from vendor
 * - Visual-only mismatch warning when amounts differ
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorBillsAPI, purchaseOrdersAPI, vendorsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Building2,
  ShoppingBag,
  Loader2,
  ArrowLeft,
  Edit2,
  DollarSign,
  Calendar,
  CreditCard,
  Ban,
  CheckCircle,
  Clock,
  Eye,
  Save,
  AlertTriangle,
  Link as LinkIcon,
  Search,
  Receipt,
  RefreshCw,
} from 'lucide-react';
import RelatedPaymentsTable from '../../components/admin/RelatedPaymentsTable';
import ChecksSection from '../../components/admin/ChecksSection';

// Status configuration (NO partial status)
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  unpaid: { label: 'Unpaid', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-500', icon: Ban },
};

// Format currency (from cents)
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const BillDetailPage = ({ 
  billId, 
  onBack, 
  onNavigateToVendor,
  onNavigateToPO,
  onNavigateToPayment,
  onRecordPayment, // Callback to open payment modal with bill pre-linked
}) => {
  const { isAdmin } = useAuth();
  
  // Data state
  const [bill, setBill] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [linkedPO, setLinkedPO] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // PO search state for linking
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [poSearchResults, setPoSearchResults] = useState([]);
  const [searchingPOs, setSearchingPOs] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  
  // Final Amount Modal state
  const [showFinalAmountModal, setShowFinalAmountModal] = useState(false);
  const [finalAmountValue, setFinalAmountValue] = useState('');
  const [savingFinalAmount, setSavingFinalAmount] = useState(false);
  
  // Computed
  const isDraft = bill?.status === 'draft';
  const isFinalized = bill?.status === 'unpaid' || bill?.status === 'finalized';
  const isEditable = bill?.status !== 'paid' && bill?.status !== 'void';
  const canEdit = bill?.status === 'draft'; // Only draft bills can be edited
  const canFinalize = isDraft && bill?.amount_cents > 0;
  const canReopen = isFinalized && (bill?.amount_paid_cents || 0) === 0;
  const canVoid = bill?.status !== 'void' && (bill?.amount_paid_cents || 0) === 0;
  const canSyncFromPO = isDraft && linkedPO && bill?.amount_cents !== linkedPO?.total_cents;
  
  // Fetch bill data
  const fetchBill = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await vendorBillsAPI.getBill(billId);
      setBill(data.bill);
      setVendor(data.vendor);
      setLinkedPO(data.po);
      setPayments(data.payments || []);
      
    } catch (err) {
      setError(err.message || 'Failed to load bill');
      toast.error(err.message || 'Failed to load bill');
    } finally {
      setLoading(false);
    }
  }, [billId]);
  
  useEffect(() => {
    if (billId) {
      fetchBill();
    }
  }, [billId, fetchBill]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchBill();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchBill,
    enabled: !loading && !!billId,
    refreshing,
    throttleMs: 1500,
  });
  
  // Open edit modal
  const openEditModal = () => {
    setEditData({
      bill_number: bill?.bill_number || '',
      bill_date: bill?.bill_date?.split('T')[0] || '',
      due_date: bill?.due_date?.split('T')[0] || '',
      amount_cents: bill?.amount_cents || 0,
      memo: bill?.memo || '',
      po_id: bill?.po_id || '',
    });
    setShowEditModal(true);
  };
  
  // Search POs for linking
  const searchPOs = async (query) => {
    if (query.length < 2) {
      setPoSearchResults([]);
      return;
    }
    
    setSearchingPOs(true);
    try {
      // Search POs from same vendor
      const data = await purchaseOrdersAPI.getPurchaseOrders({
        vendor_id: vendor?.id,
        q: query,
        page_size: 10,
      });
      setPoSearchResults(data.purchase_orders || data.items || []);
    } catch (err) {
      console.error('Failed to search POs:', err);
    } finally {
      setSearchingPOs(false);
    }
  };
  
  // Save bill
  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        bill_number: editData.bill_number || null,
        bill_date: editData.bill_date || null,
        due_date: editData.due_date || null,
        memo: editData.memo || null,
        po_id: editData.po_id || null,
      };
      
      // Only include amount if it hasn't changed (amount edit restrictions)
      if (editData.amount_cents !== bill.amount_cents && payments.length === 0) {
        updatePayload.amount_cents = editData.amount_cents;
      }
      
      await vendorBillsAPI.updateBill(billId, updatePayload);
      toast.success('Bill updated');
      setShowEditModal(false);
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to update bill');
    } finally {
      setSaving(false);
    }
  };
  
  // Finalize bill (draft → unpaid)
  const handleFinalizeBill = async () => {
    setActionLoading('finalize');
    try {
      await vendorBillsAPI.finalizeBill(billId);
      toast.success('Bill finalized and ready for payment');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to finalize bill');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Reopen bill to draft
  const handleReopenBill = async () => {
    if (!reopenReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    
    setActionLoading('reopen');
    try {
      await vendorBillsAPI.reopenToDraft(billId, reopenReason);
      toast.success('Bill reopened to draft');
      setShowReopenModal(false);
      setReopenReason('');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to reopen bill');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Sync bill from PO
  const handleSyncFromPO = async () => {
    setActionLoading('sync');
    try {
      await vendorBillsAPI.syncFromPO(billId);
      toast.success('Bill synced with PO');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to sync bill');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Void bill
  const handleVoidBill = async () => {
    if (!voidReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    
    setActionLoading('void');
    try {
      await vendorBillsAPI.voidBill(billId, voidReason);
      toast.success('Bill voided');
      setShowVoidModal(false);
      setVoidReason('');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to void bill');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Handle saving final amount
  const handleSaveFinalAmount = async () => {
    const amountCents = Math.round(parseFloat(finalAmountValue) * 100);
    if (!amountCents || amountCents <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSavingFinalAmount(true);
    try {
      await vendorBillsAPI.updateFinalAmount(billId, {
        is_final: true,
        final_amount_cents: amountCents,
      });
      toast.success('Final invoice amount saved');
      setShowFinalAmountModal(false);
      setFinalAmountValue('');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to save final amount');
    } finally {
      setSavingFinalAmount(false);
    }
  };
  
  // Handle removing final amount
  const handleRemoveFinalAmount = async () => {
    setSavingFinalAmount(true);
    try {
      await vendorBillsAPI.updateFinalAmount(billId, {
        is_final: false,
        final_amount_cents: null,
      });
      toast.success('Final amount removed');
      fetchBill();
    } catch (err) {
      toast.error(err.message || 'Failed to remove final amount');
    } finally {
      setSavingFinalAmount(false);
    }
  };
  
  // Open final amount modal with current value
  const openFinalAmountModal = () => {
    if (bill.is_final && bill.final_amount_cents) {
      setFinalAmountValue((bill.final_amount_cents / 100).toFixed(2));
    } else {
      setFinalAmountValue((bill.amount_cents / 100).toFixed(2));
    }
    setShowFinalAmountModal(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error || !bill) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Bills
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Bill not found'}
        </div>
      </div>
    );
  }
  
  const statusConfig = STATUS_CONFIG[bill.status] || STATUS_CONFIG.unpaid;
  const StatusIcon = statusConfig.icon;
  const isOverdue = bill.due_date && new Date(bill.due_date) < new Date() && bill.status !== 'paid' && bill.status !== 'void';
  
  return (
    <div className="space-y-6" data-testid="bill-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {bill.bill_number || `Bill ${bill.id.slice(0, 8)}`}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {isOverdue && (
                    <Badge className="bg-red-100 text-red-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Overdue
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Preview/Print PDF */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => {
              const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/vendor-bills/admin/${billId}/pdf?preview=true`;
              window.open(previewUrl, '_blank');
            }}
            data-testid="preview-bill-btn"
            title="Preview PDF"
          >
            <Eye className="w-4 h-4" />
          </Button>
          
          {/* Refresh Button */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-bill-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Sync from PO button (draft only) */}
          {canSyncFromPO && (
            <Button 
              variant="outline"
              onClick={handleSyncFromPO}
              disabled={actionLoading === 'sync'}
              data-testid="sync-from-po-btn"
            >
              {actionLoading === 'sync' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
              Sync from PO
            </Button>
          )}
          
          {/* Finalize button (draft only) */}
          {canFinalize && (
            <Button 
              variant="default"
              onClick={handleFinalizeBill}
              disabled={actionLoading === 'finalize'}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="finalize-bill-btn"
            >
              {actionLoading === 'finalize' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Finalize Bill
            </Button>
          )}
          
          {/* Record Payment button (unpaid bills with balance) */}
          {bill.status === 'unpaid' && bill.balance_cents > 0 && (
            <Button 
              variant="default"
              onClick={() => onRecordPayment?.({
                vendorId: bill.vendor_id,
                vendorName: vendor?.name,
                billId: bill.id,
                billNumber: bill.bill_number,
                billAmountCents: bill.amount_cents,
                billBalanceCents: bill.balance_cents,
              })}
              className="bg-green-600 hover:bg-green-700"
              data-testid="record-payment-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          )}
          
          {/* Reopen button (finalized/unpaid only, no payments) */}
          {canReopen && (
            <Button 
              variant="outline"
              onClick={() => setShowReopenModal(true)}
              disabled={actionLoading === 'reopen'}
              className="text-amber-600 hover:text-amber-700"
              data-testid="reopen-bill-btn"
            >
              {actionLoading === 'reopen' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit2 className="w-4 h-4 mr-2" />}
              Reopen to Draft
            </Button>
          )}
          
          {/* Void button (no payments) */}
          {canVoid && (
            <Button 
              variant="outline" 
              onClick={() => setShowVoidModal(true)}
              disabled={actionLoading === 'void'}
              className="text-red-600 hover:text-red-700"
              data-testid="void-bill-btn"
            >
              {actionLoading === 'void' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Void
            </Button>
          )}
          
          {/* Edit button (draft only) */}
          {canEdit && (
            <Button onClick={openEditModal} data-testid="edit-bill-btn">
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>
      
      {/* Status Banners */}
      {bill.status === 'draft' && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-center gap-2" data-testid="draft-bill-banner">
          <Edit2 className="w-5 h-5" />
          <span>
            This bill is in <strong>Draft</strong> status. Review and click <strong>Finalize Bill</strong> when ready for payment.
          </span>
        </div>
      )}
      {bill.status === 'void' && (
        <div className="bg-gray-100 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Ban className="w-5 h-5" />
          <span>This bill has been <strong>voided</strong> and cannot be edited.</span>
        </div>
      )}
      {bill.status === 'paid' && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span>This bill has been <strong>paid in full</strong>.</span>
        </div>
      )}
      
      {/* PO Mismatch Warning */}
      {canSyncFromPO && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center justify-between" data-testid="po-mismatch-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span>
              Bill amount ({formatCurrency(bill.amount_cents)}) differs from linked PO total ({formatCurrency(linkedPO?.total_cents)}).
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleSyncFromPO}
            disabled={actionLoading === 'sync'}
            className="text-amber-700"
          >
            {actionLoading === 'sync' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-1" />}
            Sync from PO
          </Button>
        </div>
      )}
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor Card */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                Vendor
              </h2>
              {vendor && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onNavigateToVendor?.(vendor.id)}
                  data-testid="view-vendor-btn"
                >
                  View Vendor
                </Button>
              )}
            </div>
            {vendor ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{vendor.name}</p>
                  <p className="text-gray-500 text-sm">{vendor.email || '—'}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">No vendor assigned</p>
            )}
          </div>
          
          {/* Linked PO Card */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-gray-400" />
                Linked Purchase Order
              </h2>
              {linkedPO && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onNavigateToPO?.(linkedPO.id)}
                  data-testid="view-po-btn"
                >
                  View PO
                </Button>
              )}
            </div>
            {linkedPO ? (
              <div 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onNavigateToPO?.(linkedPO.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-mono font-semibold">{linkedPO.po_number}</p>
                    <p className="text-sm text-gray-500">{formatDate(linkedPO.po_date)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold tabular-nums">{formatCurrency(linkedPO.total_cents)}</p>
                  <Badge className={
                    linkedPO.status === 'closed' ? 'bg-green-100 text-green-700' :
                    linkedPO.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }>
                    {linkedPO.status}
                  </Badge>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No PO linked to this bill</p>
                {canEdit && (
                  <Button size="sm" variant="outline" className="mt-2" onClick={openEditModal}>
                    <LinkIcon className="w-4 h-4 mr-1" />
                    Link PO
                  </Button>
                )}
              </div>
            )}
          </div>
          
          {/* Bill Details Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Bill Details</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Bill Number</p>
                <p className="font-medium">{bill.bill_number || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Bill Date</p>
                <p className="font-medium">{formatDate(bill.bill_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Due Date</p>
                <p className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                  {formatDate(bill.due_date)}
                  {isOverdue && ' (Overdue)'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </div>
            </div>
            
            {bill.memo && (
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-1">Memo</p>
                <p className="text-gray-700 whitespace-pre-wrap">{bill.memo}</p>
              </div>
            )}
          </div>
          
          {/* Payment History */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-gray-400" />
              Payment History
              {payments.length > 0 && <Badge variant="outline">{payments.length}</Badge>}
            </h2>
            
            <RelatedPaymentsTable 
              payments={payments.map(p => ({
                id: p.payment_id,
                payment_number: p.payment_number,
                payment_date: p.payment_date,
                payment_method: p.payment_method,
                reference_number: p.reference_number,
                status: p.status || 'applied',
                amount_cents: p.amount_cents,
                applied_amount_cents: p.applied_amount_cents,
              }))}
              onViewPayment={onNavigateToPayment}
              emptyMessage="No payments recorded"
              showAppliedAmount={true}
            />
          </div>

          {/* Checks Section */}
          <ChecksSection
            billId={billId}
            title="Checks for this Bill"
            limit={5}
            emptyMessage="No checks issued for this bill yet"
          />
        </div>
        
        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Amount Card with Final Invoice Support */}
          <div className="bg-white rounded-xl border p-6" data-testid="bill-amount-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-gray-400" />
              Amount
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bill Amount</span>
                <span className="tabular-nums font-medium">{formatCurrency(bill.amount_cents)}</span>
              </div>
              
              {/* Adjustment/Discount Row - Show discount when Bill Amount > Final Amount */}
              {bill.is_final && bill.amount_cents > bill.final_amount_cents ? (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Adjustment</span>
                  <span className="tabular-nums font-bold text-green-600">
                    -{formatCurrency(bill.amount_cents - bill.final_amount_cents)}
                  </span>
                </div>
              ) : !bill.is_final && isEditable ? (
                <button
                  onClick={openFinalAmountModal}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  data-testid="add-final-amount-btn"
                >
                  + Mark as final invoice amount
                </button>
              ) : null}
              
              {/* Warning if final amount is HIGHER than bill amount (unusual case) */}
              {bill.is_final && bill.final_amount_cents > bill.amount_cents && bill.status !== 'paid' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-start gap-2" data-testid="mismatch-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Final invoice exceeds original amount</p>
                    <p className="text-xs mt-1">
                      Original: {formatCurrency(bill.amount_cents)} → Final: {formatCurrency(bill.final_amount_cents)}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount Paid</span>
                <span className="tabular-nums text-green-600">{formatCurrency(bill.amount_paid_cents)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t">
                <span className="font-medium">Balance Due</span>
                <span className={`text-xl font-bold tabular-nums ${bill.balance_cents > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {formatCurrency(bill.balance_cents)}
                </span>
              </div>
              
              {/* Edit/Remove Final Amount Button */}
              {bill.is_final && isEditable && (
                <div className="pt-2 border-t flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openFinalAmountModal}
                    className="flex-1"
                    data-testid="edit-final-amount-btn"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit Final Amount
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFinalAmount}
                    disabled={savingFinalAmount}
                    className="text-gray-500 hover:text-red-600"
                    data-testid="remove-final-amount-btn"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Metadata Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Bill ID</span>
                <span className="font-mono text-xs">{bill.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{formatDate(bill.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span>{formatDate(bill.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-bill-modal">
          <DialogHeader>
            <DialogTitle>Edit Bill</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Bill Number</Label>
              <Input
                value={editData.bill_number || ''}
                onChange={(e) => setEditData({ ...editData, bill_number: e.target.value })}
                placeholder="INV-12345"
                data-testid="edit-bill-number"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bill Date</Label>
                <Input
                  type="date"
                  value={editData.bill_date || ''}
                  onChange={(e) => setEditData({ ...editData, bill_date: e.target.value })}
                  data-testid="edit-bill-date"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={editData.due_date || ''}
                  onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                  data-testid="edit-due-date"
                />
              </div>
            </div>
            
            {/* Amount - only editable if no payments */}
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={(editData.amount_cents / 100).toFixed(2)}
                onChange={(e) => setEditData({ ...editData, amount_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                disabled={payments.length > 0}
                data-testid="edit-amount"
              />
              {payments.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">Amount cannot be changed after payments are applied</p>
              )}
            </div>
            
            {/* PO Linking */}
            <div>
              <Label>Link to Purchase Order</Label>
              {editData.po_id ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-gray-400" />
                    <span className="font-mono">{linkedPO?.po_number || editData.po_id}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditData({ ...editData, po_id: '' })}
                    className="text-red-600"
                  >
                    Unlink
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={poSearchQuery}
                      onChange={(e) => {
                        setPoSearchQuery(e.target.value);
                        searchPOs(e.target.value);
                      }}
                      placeholder="Search PO by number..."
                      className="pl-10"
                      data-testid="po-search-input"
                    />
                  </div>
                  {searchingPOs && (
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Searching...
                    </div>
                  )}
                  {poSearchResults.length > 0 && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {poSearchResults.map(po => (
                        <button
                          key={po.id}
                          type="button"
                          onClick={() => {
                            setEditData({ ...editData, po_id: po.id });
                            setPoSearchQuery('');
                            setPoSearchResults([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 flex justify-between items-center"
                        >
                          <div>
                            <p className="font-mono font-medium">{po.po_number}</p>
                            <p className="text-sm text-gray-500">{formatDate(po.po_date)}</p>
                          </div>
                          <span className="tabular-nums">{formatCurrency(po.total_cents)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Memo */}
            <div>
              <Label>Memo</Label>
              <Textarea
                value={editData.memo || ''}
                onChange={(e) => setEditData({ ...editData, memo: e.target.value })}
                rows={3}
                placeholder="Notes about this bill..."
                data-testid="edit-memo"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-bill-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reopen to Draft Modal */}
      <Dialog open={showReopenModal} onOpenChange={setShowReopenModal}>
        <DialogContent className="max-w-md" data-testid="reopen-bill-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-500" />
              Reopen Bill to Draft
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              Reopening this bill will change its status from &quot;Unpaid&quot; back to &quot;Draft&quot;, 
              allowing you to make edits.
            </p>
            
            <div>
              <Label htmlFor="reopen-reason">Reason for reopening <span className="text-red-500">*</span></Label>
              <Textarea
                id="reopen-reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g., Need to adjust line items, incorrect amount, etc."
                className="mt-1"
                rows={3}
                data-testid="reopen-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReopenModal(false); setReopenReason(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleReopenBill} 
              disabled={actionLoading === 'reopen' || !reopenReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reopen-btn"
            >
              {actionLoading === 'reopen' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reopen to Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Void Bill Modal */}
      <Dialog open={showVoidModal} onOpenChange={setShowVoidModal}>
        <DialogContent className="max-w-md" data-testid="void-bill-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Void Bill
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              <p className="font-medium">Warning: This action cannot be undone.</p>
              <p className="text-sm mt-1">Voiding this bill will permanently mark it as void and restore any linked PO balances.</p>
            </div>
            
            <div>
              <Label htmlFor="void-reason">Reason for voiding <span className="text-red-500">*</span></Label>
              <Textarea
                id="void-reason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="e.g., Duplicate bill, vendor cancelled, etc."
                className="mt-1"
                rows={3}
                data-testid="void-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVoidModal(false); setVoidReason(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleVoidBill} 
              disabled={actionLoading === 'void' || !voidReason.trim()}
              variant="destructive"
              data-testid="confirm-void-btn"
            >
              {actionLoading === 'void' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
              Void Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Final Amount Modal */}
      <Dialog open={showFinalAmountModal} onOpenChange={setShowFinalAmountModal}>
        <DialogContent className="max-w-sm" data-testid="final-amount-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {bill.is_final ? 'Edit Final Invoice Amount' : 'Mark as Final Invoice'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Original Bill Amount</Label>
              <div className="text-lg font-medium text-gray-700 mt-1">
                {formatCurrency(bill.amount_cents)}
              </div>
            </div>
            
            <div>
              <Label>Final Invoice Amount ($)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={finalAmountValue}
                  onChange={(e) => setFinalAmountValue(e.target.value)}
                  className="pl-7 text-lg"
                  placeholder="0.00"
                  data-testid="final-amount-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter the amount from the vendor&apos;s actual invoice
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFinalAmountModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFinalAmount}
              disabled={savingFinalAmount || !finalAmountValue}
              data-testid="save-final-amount-btn"
            >
              {savingFinalAmount ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillDetailPage;
